import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  BookOpen, ShieldCheck, Users as UsersIcon, KeyRound, AlertTriangle, Info,
  Calendar, Replace, LifeBuoy, FileSearch, Phone, BookText, Sparkles, Upload,
} from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import type { CurrentUser } from '../api/types';

interface Topic {
  id: string;
  title: string;
  icon: ReactNode;
  adminOnly?: boolean;
  body: ReactNode;
}

export function HelpScreen({ user }: { user: CurrentUser }) {
  const isAdmin = user.rolle === 'admin';
  const allTopics: Topic[] = useMemo(() => buildTopics(user.vorname), [user.vorname]);
  const visibleTopics = useMemo(
    () => allTopics.filter((t) => !t.adminOnly || isAdmin),
    [allTopics, isAdmin],
  );
  const [active, setActive] = useState(visibleTopics[0]?.id ?? '');

  // Active section based on scroll position
  useEffect(() => {
    const handler = () => {
      const offsets = visibleTopics.map((t) => {
        const el = document.getElementById(`help-${t.id}`);
        if (!el) return { id: t.id, y: Infinity };
        return { id: t.id, y: Math.abs(el.getBoundingClientRect().top - 100) };
      });
      offsets.sort((a, b) => a.y - b.y);
      if (offsets[0]) setActive(offsets[0].id);
    };
    const root = document.querySelector('.page');
    root?.addEventListener('scroll', handler);
    handler();
    return () => root?.removeEventListener('scroll', handler);
  }, [visibleTopics]);

  const sections: { label: string; topics: Topic[] }[] = [
    { label: 'Einstieg', topics: visibleTopics.filter((t) => ['willkommen', 'rollen'].includes(t.id)) },
    { label: 'Alltag', topics: visibleTopics.filter((t) => ['howto-member', 'howto-import', 'howto-payment', 'howto-section', 'howto-user'].includes(t.id)) },
    { label: 'Wechsel & Übergaben', topics: visibleTopics.filter((t) => ['change-obmann', 'change-kassier', 'change-section-lead', 'change-vorstand', 'change-leave'].includes(t.id)) },
    { label: 'Wenn etwas passiert', topics: visibleTopics.filter((t) => ['locked-out', 'forgot-pw', 'lost-codes', 'lost-master-token', 'breach', 'app-down'].includes(t.id)) },
    { label: 'DSGVO & Recht', topics: visibleTopics.filter((t) => ['dsgvo-export', 'dsgvo-delete', 'dsgvo-incident', 'audit-check'].includes(t.id)) },
    { label: 'Sicherheit & Routine', topics: visibleTopics.filter((t) => ['sec-personal', 'recurring', 'backup'].includes(t.id)) },
    { label: 'Nachschlagen', topics: visibleTopics.filter((t) => ['glossary', 'contacts'].includes(t.id)) },
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(`help-${id}`);
    if (!el) return;
    const root = document.querySelector('.page');
    if (root) {
      const top = el.getBoundingClientRect().top + (root as HTMLElement).scrollTop - 16;
      (root as HTMLElement).scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setActive(id);
  };

  return (
    <div className="page-fade">
      <PageHeader
        title="Anleitung"
        subtitle="Alles was du wissen musst — geschrieben für Vorstand und Sektionsleiter, nicht für Programmierer."
      />

      <div className="help-layout">
        <aside className="help-toc">
          {sections.map((sec, i) =>
            sec.topics.length === 0 ? null : (
              <div key={sec.label}>
                {i > 0 && <div className="help-toc-section">{sec.label}</div>}
                {i === 0 && <div className="help-toc-section" style={{ marginTop: 0 }}>{sec.label}</div>}
                {sec.topics.map((t) => (
                  <a
                    key={t.id}
                    href={`#help-${t.id}`}
                    className={active === t.id ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); scrollTo(t.id); }}
                  >
                    <span style={{ width: 16, display: 'inline-flex', color: 'var(--text-tertiary)' }}>{t.icon}</span>
                    {t.title}
                  </a>
                ))}
              </div>
            ),
          )}
        </aside>

        <div className="help-content">
          {visibleTopics.map((t) => (
            <section key={t.id} id={`help-${t.id}`} className="help-section">
              <h2>{t.title}</h2>
              {t.body}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------
// Inhaltsbausteine
// --------------------------------------------------------------------

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="help-step">
      <div className="num">{n}</div>
      <div className="body">{children}</div>
    </div>
  );
}

function Callout({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger'; children: ReactNode }) {
  const Icon = tone === 'danger' ? AlertTriangle : tone === 'warn' ? AlertTriangle : Info;
  return (
    <div className={`help-callout ${tone}`}>
      <span className="ic"><Icon size={16} /></span>
      <div>{children}</div>
    </div>
  );
}

function buildTopics(firstName: string): Topic[] {
  return [
    {
      id: 'willkommen',
      title: 'Willkommen',
      icon: <Sparkles size={14} />,
      body: (
        <>
          <p>
            Hallo {firstName}, schön, dass du da bist. Diese Anleitung erklärt dir alles, was du im
            Verein mit dieser App machen kannst — vom „Heute hat Anna ihren Beitrag bezahlt"
            bis zum „Unser Obmann wechselt, wie übergeben wir das alles?"
          </p>
          <p>
            <strong>Was diese App macht:</strong> Sie verwaltet alle Mitglieder des Vereins, ihre
            Beiträge, ihre Sektionszugehörigkeit und alle Vorstands-Personen. Die Daten liegen
            verschlüsselt in der Europäischen Union (DSGVO-konform).
          </p>
          <p>
            <strong>Was sie nicht macht:</strong> Keine Trainings-Anwesenheit, keine
            Mannschaftsaufstellungen, keinen Newsletter-Versand. Lieber wenig — dafür richtig.
          </p>
          <Callout>
            Diese Anleitung ist <strong>immer</strong> über den Punkt „Anleitung" links im Menü
            erreichbar. Verlier sie nicht aus den Augen, wenn ein Wechsel ansteht.
          </Callout>
        </>
      ),
    },
    {
      id: 'rollen',
      title: 'Wer darf was?',
      icon: <ShieldCheck size={14} />,
      body: (
        <>
          <p>Vier Rollen. Jede sieht und kann nur, was sie sehen und können soll.</p>

          <h3>Admin</h3>
          <p>
            In der Regel <strong>Obmann und Kassier</strong>. Sehen den ganzen Verein und dürfen
            alles — auch Mitglieder endgültig löschen und Benutzer anlegen. Davon muss es
            <strong> immer mindestens zwei</strong> geben; die App verhindert, dass der letzte
            Admin herabgestuft oder deaktiviert wird.
          </p>

          <h3>Sektionsleiter</h3>
          <p>
            Verwaltet die ihm zugewiesenen Sektionen — z. B. <em>Fußball</em>. Sieht ausschließlich
            Mitglieder dieser Sektionen, kann sie anlegen, ändern und archivieren. Kann keine
            Mitglieder endgültig löschen und keine Benutzer verwalten.
          </p>

          <h3>Stellvertreter</h3>
          <p>
            Wie ein Sektionsleiter, kann aber keine Mitglieder endgültig löschen.
            Gedacht für Co-Trainer oder Schriftführer der Sektion.
          </p>

          <h3>Read-Only</h3>
          <p>
            Nur Lesezugriff — typischerweise für <strong>Rechnungsprüfer</strong>. Sehen alles,
            ändern nichts.
          </p>

          <Callout>
            Die Rollen werden serverseitig geprüft. Selbst wenn jemand technisch herum-tricksen
            würde, kann er nicht über seine Rolle hinaus etwas ändern.
          </Callout>
        </>
      ),
    },

    // -------- Alltag --------
    {
      id: 'howto-member',
      title: 'Mitglied verwalten',
      icon: <UsersIcon size={14} />,
      body: (
        <>
          <h3>Neues Mitglied anlegen</h3>
          <Step n={1}>Links im Menü auf <strong>„Mitglieder"</strong>.</Step>
          <Step n={2}>Oben rechts auf <strong>„Neues Mitglied"</strong>.</Step>
          <Step n={3}>Im Wizard durch die 4 Schritte: Persönliches, Kontakt, Sektionen, Übersicht.</Step>
          <Step n={4}>Pflichtfelder haben ein rotes Sternchen (<strong>*</strong>). Nur Vor- und Nachname sind Pflicht — alles andere kannst du später ergänzen.</Step>
          <Step n={5}>„Mitglied speichern" — fertig. Das neue Mitglied erscheint in der Liste.</Step>

          <h3>Mitglied bearbeiten</h3>
          <p>Klick in der Liste auf eine Zeile. Der Inspector öffnet sich rechts.</p>
          <ul>
            <li><strong>Stift-Icon</strong> oben rechts → öffnet das Bearbeiten-Fenster (Stammdaten, Verein).</li>
            <li>Tab <strong>„Notizen"</strong> für freitext Bemerkungen (wie damals die „Veränderungen"-Spalte in der Excel-Liste).</li>
            <li>Tab <strong>„Verlauf"</strong> zeigt, wer wann was an diesem Mitglied geändert hat.</li>
          </ul>

          <h3>Sektion zuordnen / entfernen</h3>
          <p>Im Inspector → Tab „Verein" → unten bei „Sektionen" das Plus-Icon klicken bzw. das X neben einer Mitgliedschaft.</p>
          <p>Klick auf die Status-Pille (z. B. „Mitglied") → daraus wird ein Dropdown: Mitglied / aktiv / Funktionär / ruhend.</p>

          <h3>Mitglied archivieren oder löschen</h3>
          <ul>
            <li><strong>Archivieren</strong>: Mitglied tritt aus dem Verein aus. Daten bleiben erhalten, über „Filter: Status = ausgetreten" weiterhin sichtbar. Versehentlich passiert? Mitglied öffnen → <strong>„Austritt zurücknehmen"</strong>.</li>
            <li><strong>Endgültig löschen</strong> (nur Admin, zweistufige Bestätigung): Name, Adresse und Kontaktdaten werden anonymisiert. Das ist <em>nicht</em> umkehrbar.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'howto-import',
      title: 'Mitglieder aus Excel importieren',
      icon: <Upload size={14} />,
      body: (
        <>
          <p>
            Für größere Listen: Die Vorlage ausfüllen (oder von den Sektionsleitern
            ausfüllen lassen) und hochladen, statt jedes Mitglied einzeln anzulegen.
          </p>

          <h3>So geht's</h3>
          <Step n={1}>Mitglieder → oben rechts <strong>„Importieren"</strong>.</Step>
          <Step n={2}>Ausgefüllte Excel-Datei ins Feld ziehen oder anklicken und auswählen.</Step>
          <Step n={3}>
            Die <strong>Vorschau</strong> zeigt Zeile für Zeile, was passieren wird — neu,
            Sektion ergänzt, schon erfasst oder Fehler. Es ist noch <em>nichts</em> gespeichert.
          </Step>
          <Step n={4}>Passt es? Auf „… Einträge importieren" klicken.</Step>

          <h3>Was der Import automatisch erledigt</h3>
          <ul>
            <li>Wer <strong>in zwei Sektionen</strong> ist, wird zu einem Mitglied mit zwei Sektionen zusammengeführt — auch wenn zwei Sektionsleiter ihn getrennt eintragen.</li>
            <li><strong>Dieselbe Datei zweimal</strong> hochladen schadet nicht: bereits Angelegte werden als „schon erfasst" erkannt.</li>
            <li>Unter 18 Jahren wird automatisch <strong>Jugendmitglied</strong> gesetzt.</li>
          </ul>

          <Callout tone="info">
            Fehlerhafte Zeilen halten den Import nicht auf — sie werden mit Zeilennummer
            und Grund benannt und übersprungen. Einfach in der Datei korrigieren und
            nochmal hochladen.
          </Callout>

          <Callout tone="warn">
            Pflicht sind nur <strong>Sektion, Vorname, Nachname</strong>. Alles andere darf
            leer bleiben — insbesondere das Eintrittsdatum, wenn es niemand mehr weiß.
          </Callout>
        </>
      ),
    },
    {
      id: 'howto-payment',
      title: 'Beitrag erfassen',
      icon: <BookText size={14} />,
      body: (
        <>
          <p>
            Es geht um den <strong>Union-Beitrag</strong>: Ab dem vollendeten 18. Lebensjahr
            zahlt jedes aktive Mitglied jährlich einen festen Betrag an die Union-Hauptkassa.
            Jugend- und Ehrenmitglieder zahlen ihn nicht.
          </p>

          <h3>Abrechnungsformular einer Sektion erzeugen</h3>
          <Step n={1}>Links im Menü auf <strong>„Sektionen"</strong>.</Step>
          <Step n={2}>Bei der gewünschten Sektion auf das <strong>Dokument-Symbol</strong> klicken.</Step>
          <Step n={3}>Das Formular öffnet sich druckfertig: Anzahl der beitragspflichtigen Mitglieder × Betrag = Gesamtsumme der Sektion.</Step>

          <Callout tone="info">
            Der Betrag pro Mitglied lässt sich unter <strong>Einstellungen</strong> ändern
            (Standard € 2,50). Die Mitgliederstatistik sollte bis <strong>30. November</strong>
            aktuell sein — danach wird abgerechnet und der Betrag im Dezember überwiesen.
          </Callout>

          <Callout tone="warn">
            Bankdaten (IBAN) werden bewusst <strong>nicht</strong> erfasst. Der Zahlungsverkehr
            läuft weiterhin außerhalb der App.
          </Callout>
        </>
      ),
    },
    {
      id: 'howto-section',
      title: 'Sektion verwalten',
      icon: <ShieldCheck size={14} />,
      adminOnly: true,
      body: (
        <>
          <h3>Neue Sektion gründen</h3>
          <Step n={1}>Menü → „Sektionen" → oben rechts „Sektion hinzufügen".</Step>
          <Step n={2}>Name, Farbe (für den Punkt in der Mitgliederliste), Icon, Standard-Beitrag eingeben.</Step>
          <Step n={3}>Sektion ist sofort verfügbar — Mitglieder können ihr zugeordnet werden.</Step>

          <h3>Bestehende Sektion bearbeiten</h3>
          <p>Auf der Sektionsseite das Stift-Icon einer Karte klicken — Name, Farbe, Icon und Beitrag lassen sich ändern.</p>

          <h3>Sektion auflösen</h3>
          <p>Im Bearbeiten-Modal den Schalter <strong>„Sektion aktiv"</strong> auf aus stellen. Die Sektion wird in Listen abgeblendet, bleibt aber für Statistik und alte Beitragsdaten erhalten.</p>

          <Callout tone="warn">
            Eine Sektion <strong>komplett zu löschen</strong> ist absichtlich nicht möglich — sonst
            würden Mitgliedschaftsverläufe und Beitragshistorien zerreißen.
          </Callout>
        </>
      ),
    },
    {
      id: 'howto-user',
      title: 'Benutzer einladen',
      icon: <KeyRound size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>
            „Benutzer" sind Personen mit Login (typisch: Vorstandsmitglieder, Sektionsleiter).
            Die Mitglieder selbst haben <strong>keinen</strong> Login.
          </p>

          <h3>Neuen Benutzer anlegen</h3>
          <Step n={1}>Menü → „Benutzer" → oben rechts „Neuer Benutzer".</Step>
          <Step n={2}>5-Schritt-Wizard: Basis-Daten, Rolle, Sektionen, Berechtigungen, Bestätigung.</Step>
          <Step n={3}>Am Ende zeigt die App ein <strong>temporäres Passwort</strong> wie „Adler-Wiese-7392" — das ist absichtlich gut vorlesbar.</Step>
          <Step n={4}>Übergabe per WhatsApp, E-Mail oder am Telefon. Das Passwort wird <strong>nur einmal</strong> angezeigt — kopier es jetzt.</Step>
          <Step n={5}>Die eingeladene Person meldet sich damit an und muss sofort ein eigenes Passwort festlegen.</Step>

          <h3>Passwort eines Benutzers zurücksetzen</h3>
          <p>Klick auf den Benutzer → Sicherheit → „Passwort zurücksetzen". Ein neues Temp-Passwort wird erzeugt. Alle aktuellen Sitzungen dieser Person werden sofort beendet.</p>

          <h3>Benutzer deaktivieren / reaktivieren</h3>
          <p>Im Benutzer-Inspector unten der rote Button „Benutzer deaktivieren". Der Account bleibt erhalten (wegen Audit-Log), kann sich aber nicht mehr anmelden. Später durch „Reaktivieren" wieder freischalten.</p>

          <Callout tone="warn">
            Den <strong>letzten aktiven Admin</strong> kannst du nicht deaktivieren — die App blockiert das.
          </Callout>
        </>
      ),
    },

    // -------- Wechsel & Übergaben --------
    {
      id: 'change-obmann',
      title: 'Obmann-Wechsel',
      icon: <Replace size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>Der wichtigste Wechsel. Sicher abwickeln — Schritt für Schritt.</p>

          <Step n={1}>
            <strong>Neuen Obmann als Admin anlegen</strong> (du oder der Kassier): Menü „Benutzer" → „Neuer Benutzer"
            → Rolle „Admin". Temp-Passwort übergeben.
          </Step>
          <Step n={2}>
            <strong>Neuer Obmann meldet sich an</strong>, ändert das Passwort, erhält 10 Recovery-Codes.
            <strong> Diese ausdrucken und im Vereinstresor verwahren.</strong>
          </Step>
          <Step n={3}>
            <strong>Funktionen anpassen</strong>: Beim neuen Obmann „Funktion: Obmann" eintragen, beim alten
            auf z. B. „Ehrenobmann" ändern.
          </Step>
          <Step n={4}>
            <strong>Notfall-Schlüssel rotieren</strong>: Einstellungen → Sicherheit → „Notfall-Schlüssel
            Rotieren". App zeigt neuen Klartext (für Tresor) und einen Hash. Hash an den
            technischen Betreuer schicken — er muss ihn auf dem Server eintragen, sonst funktioniert
            der neue Token noch nicht.
          </Step>
          <Step n={5}>
            <strong>Impressum aktualisieren</strong> (steht im Code; der technische Betreuer macht das): Namen
            des neuen Vorstands eintragen.
          </Step>
          <Step n={6}>
            <strong>Alten Obmann deaktivieren</strong>: Im Benutzer-Inspector → „Benutzer deaktivieren".
            Audit-Trail bleibt erhalten.
          </Step>
          <Step n={7}>
            <strong>Kontaktdaten</strong> im „Passwort vergessen"-Screen aktualisieren (technischer Betreuer).
          </Step>

          <Callout tone="warn">
            Vor dem Deaktivieren des alten Obmanns immer <strong>mindestens 2 andere aktive
            Admins</strong> sicherstellen — z. B. neuer Obmann + Kassier. Die App lässt es sonst
            nicht zu.
          </Callout>
        </>
      ),
    },
    {
      id: 'change-kassier',
      title: 'Kassier-Wechsel',
      icon: <Replace size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>Gleicher Ablauf wie der Obmann-Wechsel, aber:</p>
          <ul>
            <li>Den <strong>Notfall-Schlüssel musst du nicht zwingend rotieren</strong> — nur bei Verdacht auf Kompromittierung oder wenn der Kassier den Klartext gesehen hat.</li>
            <li>Bankvollmachten, Kassenbuch und sonstiges <strong>außerhalb der App</strong> mit dem neuen Kassier regeln.</li>
            <li>Die App-Rolle des Kassiers ist <em>Admin</em> wie beim Obmann.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'change-section-lead',
      title: 'Sektionsleiter-Wechsel',
      icon: <Replace size={14} />,
      adminOnly: true,   // erfordert Benutzerverwaltung — nur Admin
      body: (
        <>
          <Step n={1}>Neuen Sektionsleiter als Benutzer anlegen → Rolle „Sektionsleiter", die betreffende Sektion zuweisen.</Step>
          <Step n={2}>Temp-Passwort übergeben — neuer Sektionsleiter ändert es beim ersten Login.</Step>
          <Step n={3}>Beim alten Sektionsleiter: entweder die Sektion entfernen oder ihn auf „Stellvertreter" demoten.</Step>
          <Step n={4}>Im Mitglied-Datensatz: Funktion „Sektionsleiter Fußball" auf die neue Person umtragen.</Step>
        </>
      ),
    },
    {
      id: 'change-vorstand',
      title: 'Kompletter Vorstandswechsel',
      icon: <UsersIcon size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>Typisch nach der Jahreshauptversammlung. Reihenfolge ist wichtig:</p>
          <Step n={1}>
            <strong>Bevor jemand zurücktritt</strong>: alle neuen Vorstandsmitglieder als Admin anlegen.
            Sie melden sich an, drucken ihre Recovery-Codes.
          </Step>
          <Step n={2}>
            Sicherstellen: Mindestens drei aktive Admins gleichzeitig (alt + neu) während der
            Übergabe.
          </Step>
          <Step n={3}>
            <strong>Übergabe-Termin</strong>: alte und neue Personen physisch zusammen. Tresor
            öffnen, Inhalte zeigen, neuen Notfall-Schlüssel rotieren.
          </Step>
          <Step n={4}>
            Alte Admins der Reihe nach deaktivieren — nie alle gleichzeitig.
          </Step>
          <Step n={5}>
            Technische Eigentümer-Übergabe (Cloudflare-Account, GitHub-Repo, Domain) durch den
            technischen Betreuer.
          </Step>
        </>
      ),
    },
    {
      id: 'change-leave',
      title: 'Person plötzlich weg',
      icon: <Phone size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>
            Wenn ein Vorstandsmitglied unerwartet nicht mehr verfügbar ist (Krankheit, Todesfall,
            unauffindbar):
          </p>
          <Step n={1}>Sofort einen anderen aktiven Admin verständigen.</Step>
          <Step n={2}>Im Benutzer-Inspector der betroffenen Person → „Auf allen Geräten abmelden", dann „Passwort zurücksetzen" (rein vorsorglich), zuletzt „Deaktivieren".</Step>
          <Step n={3}>Falls die Person Admin war: sofort einen Ersatz anlegen, damit das 2-Admin-Prinzip wieder erfüllt ist.</Step>
          <Callout tone="danger">
            Wenn niemand sonst Admin-Rechte hat: der Notfall-Schlüssel aus dem Tresor erlaubt
            es, im Setup-Wizard einen neuen Admin anzulegen. Siehe unten „Keiner kann sich anmelden".
          </Callout>
        </>
      ),
    },

    // -------- Wenn etwas passiert --------
    {
      id: 'forgot-pw',
      title: 'Passwort vergessen',
      icon: <KeyRound size={14} />,
      body: (
        <>
          <Step n={1}>
            <strong>Einen Admin bitten</strong> (Obmann oder Kassier) — der setzt dein Passwort
            zurück und gibt dir ein neues Temp-Passwort, das du beim nächsten Login änderst.
            Für Sektionsleiter und Stellvertreter ist das der normale Weg.
          </Step>
          <Step n={2}>
            <strong>Nur als Admin:</strong> Login-Seite → „Recovery-Code verwenden" → E-Mail
            plus einer deiner 10 ausgedruckten Codes. Jeder Code funktioniert genau einmal.
          </Step>
        </>
      ),
    },
    {
      id: 'locked-out',
      title: 'Du bist gesperrt',
      icon: <AlertTriangle size={14} />,
      body: (
        <>
          <p>
            Nach 5 falschen Passwort-Versuchen sperrt die App den Account für 5 Minuten.
            Einfach warten und es nochmal versuchen — der Countdown läuft am Login-Banner.
          </p>
          <p>
            Wenn du sicher bist, dass du das Passwort kennst, aber es trotzdem nicht klappt:
            ein anderer Admin kann das Passwort zurücksetzen (siehe oben).
          </p>
          <Callout tone="info">
            Die Sperre verhindert Brute-Force-Angriffe. Wenn du plötzlich mehrfach gesperrt
            wirst ohne es selbst zu verursachen, könnte jemand versuchen, sich als du
            anzumelden — informiere den technischen Betreuer.
          </Callout>
        </>
      ),
    },
    {
      id: 'lost-codes',
      title: 'Recovery-Codes weg',
      icon: <LifeBuoy size={14} />,
      adminOnly: true,   // Recovery-Codes gibt es ausschließlich für Admins
      body: (
        <>
          <p>
            <strong>Bist du noch eingeloggt?</strong> Einstellungen → Sicherheit → Recovery-Codes
            → „Neu generieren". Die alten werden ungültig. Drucken und in den Tresor.
          </p>
          <p>
            <strong>Bist du nicht eingeloggt und kommst gar nicht rein?</strong> Anderen Admin bitten,
            dein Passwort zurückzusetzen (siehe „Passwort vergessen"). Nach dem Login dann
            neue Codes generieren.
          </p>
        </>
      ),
    },
    {
      id: 'lost-master-token',
      title: 'Notfall-Schlüssel weg',
      icon: <KeyRound size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>
            Der Notfall-Schlüssel (Master-Token) ist der „Schlüssel zum Schlüsseltresor". Wenn er
            verloren geht, solange noch ein Admin sich anmelden kann:
          </p>
          <Step n={1}>Als Admin anmelden.</Step>
          <Step n={2}>Einstellungen → Sicherheit → Notfall-Schlüssel → „Rotieren".</Step>
          <Step n={3}>Neuer Klartext in den Tresor. Hash an den technischen Betreuer.</Step>
          <Callout tone="danger">
            Wenn der Notfall-Schlüssel weg ist <strong>und</strong> niemand mehr Admin-Login
            hat: nur direkter Datenbank-Zugriff (durch den technischen Betreuer) kann die App
            wieder zugänglich machen. Siehe Disaster-Recovery-Dokument.
          </Callout>
        </>
      ),
    },
    {
      id: 'breach',
      title: 'Verdacht: jemand fremdes hat Zugriff',
      icon: <AlertTriangle size={14} />,
      body: (
        <>
          <p>Wenn du den Verdacht hast, dass dein Account gehackt wurde:</p>
          <Step n={1}>Einstellungen → Sicherheit → „Auf allen Geräten abmelden" — beendet alle Sitzungen sofort.</Step>
          <Step n={2}>Direkt danach „Passwort ändern" mit einem neuen, starken Passwort.</Step>
          <Step n={3}>Im Aktivitätsprotokoll prüfen, welche Aktionen unter deinem Namen passiert sind.</Step>
          <Step n={4}>Bei Verdacht auf größeren Vorfall: technischen Betreuer informieren, Datenschutzbehörde wenn personenbezogene Daten betroffen.</Step>
        </>
      ),
    },
    {
      id: 'app-down',
      title: 'App geht nicht / ist langsam',
      icon: <AlertTriangle size={14} />,
      body: (
        <>
          <ul>
            <li><strong>Lädt gar nicht:</strong> dein Internet prüfen. Footer unten zeigt „Offline" wenn deine Verbindung weg ist.</li>
            <li><strong>Lädt langsam:</strong> beim ersten Aufruf können ein paar Sekunden vergehen, danach ist es schnell. Wenn es dauerhaft langsam ist, technischen Betreuer informieren.</li>
            <li><strong>Fehler-Meldung</strong> beim Speichern: oft ist das ein temporäres Problem. Seite neu laden, dann nochmal versuchen.</li>
          </ul>
        </>
      ),
    },

    // -------- DSGVO & Recht --------
    {
      id: 'dsgvo-export',
      title: 'Mitglied verlangt Auskunft',
      icon: <FileSearch size={14} />,
      body: (
        <>
          <p>
            Jedes Mitglied hat laut DSGVO Anspruch auf eine vollständige Auskunft darüber, was
            wir über es gespeichert haben (Art. 15 DSGVO).
          </p>
          <Step n={1}>Mitglied im Inspector öffnen.</Step>
          <Step n={2}>Unten <strong>„PDF-Auskunft"</strong> klicken → öffnet eine druckbare Seite mit allen Daten + Beitragshistorie. Als PDF speichern oder ausdrucken und dem Mitglied geben.</Step>
          <Step n={3}>Zusätzlich „JSON-Export" für die Roh-Daten, falls jemand das technisch braucht.</Step>
          <Callout>
            Der Versand der Auskunft muss <strong>innerhalb eines Monats</strong> nach Anfrage
            erfolgen (kann auf 3 Monate verlängert werden bei komplexen Anfragen).
          </Callout>
        </>
      ),
    },
    {
      id: 'dsgvo-delete',
      title: 'Mitglied verlangt Löschung',
      icon: <FileSearch size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>
            Heikles Thema: aktive Mitgliedschaft → Löschung ist meist <strong>nicht</strong>
            zulässig (Vertrag, berechtigtes Interesse). Erst die Mitgliedschaft beenden, dann löschen.
          </p>

          <h3>Ausgetretenes Mitglied:</h3>
          <Step n={1}>Mitglied im Inspector öffnen.</Step>
          <Step n={2}>Unten „Löschen" → zweistufige Bestätigung (das ist Absicht, damit man nicht aus Versehen klickt).</Step>
          <Step n={3}>Personenbezogene Felder (Name, E-Mail, Adresse, Telefon) werden anonymisiert.</Step>

          <Callout tone="warn">
            Beitrags- und Buchungsdaten <strong>müssen 7 Jahre aufbewahrt werden</strong> (§ 132 BAO).
            Deshalb anonymisiert die App das Mitglied, statt es wirklich zu löschen. Nach 7 Jahren
            ohne Beitragsbewegung kann auch der anonyme Datensatz physisch gelöscht werden.
          </Callout>
        </>
      ),
    },
    {
      id: 'dsgvo-incident',
      title: 'Datenpanne / Vorfall',
      icon: <AlertTriangle size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>
            Wenn personenbezogene Daten unbefugt zugänglich waren (z. B. ein Admin-Account gehackt,
            ein Backup-CSV ist verloren gegangen):
          </p>
          <Step n={1}>Innerhalb <strong>72 Stunden</strong> an die Datenschutzbehörde melden (<a href="https://dsb.gv.at" target="_blank" rel="noreferrer" className="text-accent">dsb.gv.at</a>).</Step>
          <Step n={2}>Bei hohem Risiko: betroffene Mitglieder informieren (Art. 34 DSGVO).</Step>
          <Step n={3}>Alle Schlüssel rotieren, alle Admin-Passwörter zurücksetzen. Technischer Betreuer übernimmt.</Step>
        </>
      ),
    },
    {
      id: 'audit-check',
      title: 'Vereinsprüfung',
      icon: <FileSearch size={14} />,
      adminOnly: true,
      body: (
        <>
          <p>Wenn die Bezirkshauptmannschaft (BH) eine Vereinsprüfung durchführt:</p>
          <ul>
            <li>Menü „Benutzer" → Tab „Aktivitätsprotokoll" → „CSV" für vollständigen Audit-Log.</li>
            <li>Einstellungen → Daten & Sicherung → „Manuelles Backup" für komplette Datensicherung als CSV.</li>
            <li>Alle Daten liegen DSGVO-konform in der EU (Cloudflare D1, jurisdiction = eu).</li>
          </ul>
        </>
      ),
    },

    // -------- Sicherheit & Routine --------
    {
      id: 'sec-personal',
      title: 'Deine persönliche Sicherheit',
      icon: <ShieldCheck size={14} />,
      body: (
        <>
          <ul>
            <li><strong>Recovery-Codes drucken</strong>. Niemals nur am Bildschirm aufschreiben.</li>
            <li><strong>Im Tresor verwahren</strong> — nicht in der E-Mail, nicht im Cloud-Speicher.</li>
            <li><strong>Passwort regelmäßig ändern</strong>: alle 6–12 Monate. Drei zufällige Wörter sind oft sicherer und einfacher zu merken.</li>
            <li><strong>Sessions checken</strong>: Einstellungen → Sicherheit → Aktive Sitzungen. Unbekannte Sitzungen sofort beenden.</li>
            <li><strong>Bei Verdacht</strong>: „Auf allen Geräten abmelden" und Passwort ändern.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'recurring',
      title: 'Was wann erledigen',
      icon: <Calendar size={14} />,
      adminOnly: true,   // fast alles davon kann nur der Admin
      body: (
        <>
          <h3>30. November — der wichtige Termin</h3>
          <p>
            Bis dahin muss die Mitgliederstatistik jeder Sektion aktuell sein. Danach das
            Abrechnungsformular je Sektion erzeugen (Sektionen → Dokument-Symbol) und den
            Sektionsleitern schicken. Überweisung an die Union-Hauptkassa im Dezember.
          </p>

          <h3>März</h3>
          <p>Jahreshauptversammlung — bei Vorstandswechsel siehe „Kompletter Vorstandswechsel".</p>

          <h3>Regelmäßig</h3>
          <ul>
            <li><strong>Quartalsweise:</strong> Backup-CSV ziehen und sicher ablegen.</li>
            <li><strong>Halbjährlich:</strong> Aktivitätsprotokoll überfliegen — ungewöhnliche Logins?</li>
            <li><strong>Jährlich:</strong> Notfall-Schlüssel rotieren.</li>
            <li><strong>Alle 2 Jahre:</strong> Recovery-Codes neu erzeugen, ausdrucken, alten Ausdruck vernichten.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'backup',
      title: 'Backup & Datensicherung',
      icon: <BookOpen size={14} />,
      adminOnly: true,
      body: (
        <>
          <h3>Automatisch</h3>
          <p>
            Cloudflare macht im Hintergrund täglich Snapshots der Datenbank (30 Tage rückwirkend
            wiederherstellbar). Diese laufen in EU-Rechenzentren.
          </p>

          <h3>Manuell</h3>
          <Step n={1}>Einstellungen → Daten & Sicherung → „Manuelles Backup".</Step>
          <Step n={2}>Eine CSV-Datei wird heruntergeladen — alle Tabellen drin (Mitglieder, Sektionen, Beiträge, Benutzer).</Step>
          <Step n={3}>Auf einem sicheren, vereinseigenen Speicher ablegen (verschlüsselte USB, NAS, ...).</Step>

          <Callout tone="warn">
            Die Datei enthält <strong>alle Mitgliederdaten im Klartext</strong> — bitte nicht
            per E-Mail verschicken und nicht ungeschützt ablegen.
          </Callout>
        </>
      ),
    },

    // -------- Nachschlagen --------
    {
      id: 'glossary',
      title: 'Glossar',
      icon: <BookOpen size={14} />,
      body: (
        <>
          <dl className="help-glossary">
            <dt>Archivieren</dt>
            <dd>Das Mitglied ist aus dem Verein ausgetreten. Die Daten bleiben erhalten und die Aktion lässt sich mit „Austritt zurücknehmen" wieder rückgängig machen — anders als „Endgültig löschen".</dd>

            <dt>Temp-Passwort</dt>
            <dd>Vorläufiges Passwort wie „Adler-Wiese-7392", das beim Anlegen eines Benutzers erzeugt wird. Muss beim ersten Login gegen ein eigenes getauscht werden.</dd>

            <dt>Sitzung</dt>
            <dd>Dein Login auf einem bestimmten Gerät. Du kannst auf mehreren Geräten gleichzeitig angemeldet sein und einzelne Sitzungen in den Einstellungen beenden.</dd>

            <dt>Aktivitätsprotokoll</dt>
            <dd>Zeigt, wer wann was geändert hat. Du siehst deine eigenen Aktionen, der Admin sieht alle.</dd>

            <dt>Bankdaten</dt>
            <dd>Werden bewusst nicht erfasst. Der Union-Beitrag läuft über das Abrechnungsformular je Sektion, der Zahlungsverkehr außerhalb der App.</dd>
          </dl>
        </>
      ),
    },
    {
      id: 'contacts',
      title: 'Wer hilft im Notfall',
      icon: <Phone size={14} />,
      body: (
        <>
          <p>Eskalation in dieser Reihenfolge, wenn etwas nicht geht:</p>
          <Step n={1}><strong>Anderer aktiver Admin</strong> im Verein (Obmann / Kassier).</Step>
          <Step n={2}><strong>Technischer Betreuer</strong> — Telefon und E-Mail stehen im Vereinstresor auf der gedruckten Notfall-Liste.</Step>
          <Step n={3}><strong>Cloudflare-Support</strong> für Account-Wiederherstellung. Antwortzeit ~24 Stunden.</Step>
          <Step n={4}><strong>Datenschutzbehörde</strong> (<a href="https://dsb.gv.at" target="_blank" rel="noreferrer" className="text-accent">dsb.gv.at</a>) — bei Datenpannen innerhalb 72 Stunden.</Step>
          <Callout>
            Alle Telefonnummern und Zugangsdaten müssen im Vereinstresor liegen. Drucke
            die <em>DISASTER_RECOVERY.md</em> aus dem Repo aus und lege sie dazu —
            sie enthält alle Details, die im echten Notfall gebraucht werden.
          </Callout>
        </>
      ),
    },
  ];
}
